import React, { useCallback, useMemo, useState } from 'react';

import type { SuggestedEditKind, UUID } from '@aline/common';
import { getStartingPosition, Select } from '@aline/common';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, IconButton, Skeleton } from '@mui/material';
import { CirclePlus, GripVertical, Trash2 } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { makeStyles } from 'tss-react/mui';

import { RULE_GUTTER_PX } from './RuleRow';
import { SortableRuleRow } from './SortableRuleRow';
import {
  findStop,
  interleaveByRank,
  MoveSuggestionStrip,
  SuggestedTextDiff,
  SUGGESTION_CARD_TONES,
  SuggestionDecision,
  SuggestionPill,
  useMoveHover,
  useStopDecision,
  useSuggestionReview,
} from './suggested-edits';
import type { RuleGhost, SuggestionStop } from './suggested-edits';
import type { PlaybookDetailCategory, PlaybookDetailRule } from './types';

/** Why "Add Rule" is off in a category that is itself the suggestion, by the card's kind. */
const ADD_RULE_DISABLED_REASONS: Partial<Record<SuggestedEditKind, string>> = {
  add: 'Accept the new category before adding rules to it.',
  delete: 'This category is proposed for removal.',
};

/** The category name's line height, which the decision beside it centres on. */
const NAME_LINE_HEIGHT = 24;

const useStyles = makeStyles<
  { current: boolean; kind: SuggestedEditKind | null },
  'dragHandleIcon' | 'headerButton'
>()((theme, { current, kind }, classes) => {
  /**
   * A title change shows only once the header is the current stop: the
   * header takes the tint and a ring of its own, and the card's border stays
   * gray.
   */
  const cardKind = getCardKind(kind);
  const cardTone = cardKind ? SUGGESTION_CARD_TONES[cardKind] : null;
  const headerTone = kind === 'change' && current ? SUGGESTION_CARD_TONES.change : null;
  const borderColor = cardTone?.border ?? theme.appColors.rule;

  return {
    addRuleBottomButton: {
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: theme.spacing(1),
      },
      /**
       * A translucent black while disabled (in a category that is itself the
       * suggestion), so it grays against the card's tint rather than a fixed gray.
       */
      '&.Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.25)',
      },
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      borderRadius: 6,
      color: theme.appColors.primary,
      fontSize: 12,
      fontWeight: 500,
      justifyContent: 'flex-start',
      padding: theme.spacing(0.75, 1),
      textTransform: 'none',
      width: '100%',
    },
    // Wraps the button so the tooltip still works while it is disabled.
    addRuleButtonWrap: {
      display: 'flex',
      width: '100%',
    },
    /**
     * A disabled button has no hover surface to fill the row, so the wrapper
     * shrinks to the label and the tooltip sits over the words, not the row.
     */
    addRuleButtonWrapDisabled: {
      width: 'fit-content',
    },
    addRuleRow: {
      alignItems: 'center',
      display: 'flex',
      // Sits the same distance below the last rule as the rules are from each
      // other, by cancelling most of the card's gap.
      marginTop: `calc(${theme.spacing(0.75)} - ${theme.spacing(1.5)})`,
      // Indented past the number gutter so the button lines up with the rows' boxes.
      paddingLeft: RULE_GUTTER_PX,
    },
    categoryName: {
      flexGrow: 1,
      minWidth: 0,
    },
    /**
     * The pending name diff wraps rather than truncating like the editable
     * name, so the whole change is readable beside the decision.
     */
    categoryNameDiff: {
      display: 'block',
      fontSize: 16,
      fontWeight: 600,
      lineHeight: `${NAME_LINE_HEIGHT}px`,
      overflowWrap: 'anywhere',
    },
    categoryNameDisplay: {
      fontSize: 16,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    categoryNameInput: {
      fontSize: 16,
      fontWeight: 600,
    },
    categoryNamePlaceholder: {
      color: theme.appColors.secondaryType,
    },
    /**
     * The handle for reordering categories. The span covers the whole page
     * gutter left of the card, so moving the cursor off the card towards the
     * grip still counts as hovering the card and the grip stays visible until
     * it is reached. The glyph is centered on the card; the strip is the
     * grab target.
     */
    dragHandle: {
      alignItems: 'center',
      bottom: 0,
      color: theme.appColors.secondaryType,
      cursor: 'grab',
      display: 'flex',
      justifyContent: 'center',
      left: -24,
      position: 'absolute',
      top: 0,
      touchAction: 'none',
      width: 24,
    },
    // Only the glyph fades, so the hit area above stays in place at rest.
    dragHandleIcon: {
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    // A ghost destination in the rule list, indented past the number gutter.
    ghostRow: {
      paddingLeft: RULE_GUTTER_PX,
    },
    // A card per category: the header, its rules and the add-rule row share one
    // border, matching the position cards in the details panel.
    group: {
      /**
       * Hovering anywhere in the card reveals the header's delete control, not
       * just hovering the header itself. Keyboard focus on the control reveals
       * it too.
       */
      [`&:has(.${classes.headerButton}:focus-visible) .${classes.headerButton}, &:hover .${classes.headerButton}, &:hover .${classes.dragHandleIcon}`]:
        {
          opacity: 1,
        },
      backgroundColor: cardTone ? cardTone.background : 'transparent',
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      /**
       * A whole-card suggestion that is the current stop doubles its border
       * with an outline, like the header ring, without taking layout space.
       */
      outline: current && cardTone ? `1px solid ${cardTone.border}` : undefined,
      padding: theme.spacing(1.5, 2, 1, 2),
      position: 'relative',
    },
    // The card being dragged: it is the one that moves, so it is lifted above
    // the siblings it passes and its grip reads as held.
    groupDragging: {
      [`& .${classes.dragHandleIcon}`]: {
        opacity: 1,
      },
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[4],
      cursor: 'grabbing',
      zIndex: 1,
    },
    header: {
      alignItems: 'center',
      backgroundColor: headerTone ? headerTone.background : 'transparent',
      /**
       * A border on every side, laid exactly over the card's own border: the
       * margins bleed one pixel past the card's padding to reach it, and the
       * padding comes back inside so the contents stay put. Transparent on
       * the top and sides so the card's border shows through, and the card's
       * colour on the bottom, where it is the divider. Once the header is the
       * current stop the ring turns the kind colour on all four sides, and
       * an outline doubles it, so it reads thicker without moving anything.
       */
      border: `1px solid ${headerTone ? headerTone.border : 'transparent'}`,
      borderBottomColor: headerTone ? headerTone.border : borderColor,
      borderRadius: '8px 8px 0 0',
      display: 'flex',
      gap: theme.spacing(1),
      margin: `calc(${theme.spacing(-1.5)} - 1px) calc(${theme.spacing(-2)} - 1px) 0`,
      outline: headerTone ? `1px solid ${headerTone.border}` : undefined,
      padding: theme.spacing(1.5, 2, 1),
    },
    headerButton: {
      color: theme.appColors.secondaryType,
      // Revealed on card hover/focus so the resting header is just the name.
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    /**
     * The decision is held a little in from the header's right edge, and
     * pinned to the first line of a wrapped name diff. It is as tall as that
     * line, so no offset is needed to centre it there.
     */
    headerSuggestion: {
      alignSelf: 'flex-start',
      marginRight: 9,
    },
    // The move strip sits on the card's bottom edge, below the add-rule row.
    moveStrip: {
      margin: theme.spacing(0, -2, -1),
    },
    noRules: {
      border: '1px dashed transparent',
      borderRadius: 6,
      color: theme.appColors.secondaryType,
      fontSize: 14,
      fontStyle: 'italic',
      padding: theme.spacing(0.75, 1.5),
      textAlign: 'center',
      transition: 'border-color 120ms ease, color 120ms ease',
    },
    // An empty category is the drop target itself, having no rule to stand in
    // for it, so its placeholder lights up to say the drag can end here.
    noRulesDropTarget: {
      borderColor: theme.appColors.primary,
      color: theme.appColors.primary,
    },
    ruleSkeleton: {
      borderRadius: 6,
      height: 36,
    },
    rules: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
      // A little more room under the header's divider than the card's gap gives.
      marginTop: theme.spacing(0.5),
    },
    struck: {
      textDecorationLine: 'line-through',
    },
  };
});

/** The editable category name, or its diff while a rename is pending: read-only until it resolves. */
const CategoryName = React.memo(function CategoryName({
  category,
  disabled,
  focusNameSeq,
  kind,
  onRename,
  suggestedName,
}: {
  category: PlaybookDetailCategory;
  disabled: boolean;
  focusNameSeq: number;
  kind: SuggestedEditKind | null;
  onRename: (newName: string) => void;
  suggestedName: string | undefined;
}): JSX.Element {
  const { classes, cx } = useStyles({ current: false, kind });

  if (suggestedName !== undefined) {
    return (
      <SuggestedTextDiff
        after={suggestedName}
        before={category.name}
        className={classes.categoryNameDiff}
      />
    );
  }

  return (
    <InlineEditField
      key={focusNameSeq}
      classes={{
        display: cx(classes.categoryNameDisplay, kind === 'delete' && classes.struck),
        input: classes.categoryNameInput,
        placeholder: classes.categoryNamePlaceholder,
      }}
      disabled={disabled}
      editTooltip="Rename category"
      initiallyEditing={focusNameSeq > 0}
      placeholder="Type a category title..."
      textPlaceholder="Untitled category"
      value={category.name}
      onSave={onRename}
    />
  );
});

/**
 * While a suggestion is pending on the category the accept/reject controls
 * take the delete button's place at the right edge; the suggestion is the
 * only thing to decide until it resolves.
 */
const CategoryHeaderAction = React.memo(function CategoryHeaderAction({
  disabled,
  kind,
  onDelete,
  stop,
}: {
  disabled: boolean;
  kind: SuggestedEditKind | null;
  onDelete: () => void;
  stop: SuggestionStop | null;
}): JSX.Element {
  const { classes } = useStyles({ current: false, kind });
  const decision = useStopDecision(stop);

  if (stop && kind) {
    return (
      <SuggestionDecision
        acceptDisabledReason={stop.blockedReason}
        className={classes.headerSuggestion}
        kind={kind}
        rationale={stop.rationale}
        onAccept={decision.onAccept}
        onReject={decision.onReject}
      />
    );
  }

  return (
    <AlineTooltip arrow placement="top" title="Delete category and its rules">
      <IconButton
        className={classes.headerButton}
        data-cy={Select.playbooks.categoryDelete}
        disabled={disabled}
        size="small"
        onClick={onDelete}
      >
        <Trash2 size={16} />
      </IconButton>
    </AlineTooltip>
  );
});

/**
 * The destination end of a rule move: a dashed move pill in the target slot,
 * filled in the move tone while the move is the current stop. The arrow
 * overlay pairs this row with the source.
 */
const RuleGhostRow = React.memo(function RuleGhostRow({
  ruleId,
  stop,
}: {
  ruleId: UUID;
  stop: SuggestionStop;
}): JSX.Element {
  const { classes } = useStyles({ current: false, kind: null });
  const { currentStop } = useSuggestionReview();
  const decision = useStopDecision(stop);
  const hover = useMoveHover(ruleId);

  return (
    <div
      className={classes.ghostRow}
      data-cy={Select.playbooks.suggestionGhost}
      data-move-anchor={`${ruleId}:ghost`}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
    >
      <SuggestionPill
        centered
        dashed
        acceptDisabledReason={stop.blockedReason}
        acceptTitle="Accept move"
        current={currentStop?.key === stop.key}
        kind="moveDestination"
        label="Final position"
        rationale={stop.rationale}
        rejectTitle="Reject move"
        onAccept={decision.onAccept}
        onReject={decision.onReject}
        onSelect={decision.onSelect}
      />
    </div>
  );
});

interface RuleCategoryGroupProps {
  category: PlaybookDetailCategory;
  /** Pending moves landing in this category, shown as ghost rows at their destination. */
  ghosts: readonly RuleGhost[];
  onAddRule: (categoryId: UUID) => void;
  onDeleteCategory: (categoryId: UUID) => void;
  onDeleteRule: (categoryId: UUID, ruleId: UUID) => void;
  onRenameCategory: (categoryId: UUID, newName: string) => void;
  onSelectRule: (ruleId: UUID) => void;
  /**
   * The rules to show under the category, in order. Passed separately from the
   * category because mid-drag they come from the drag's draft order rather
   * than from `category.rules`.
   */
  rules: readonly PlaybookDetailRule[];
  selectedRuleId: UUID | null;
  disabled?: boolean;
  /**
   * Open the name for editing whenever this changes to a non-zero value, for a
   * category that was just added (or re-requested via "Add category").
   */
  focusNameSeq?: number;
  /** One trailing rule-row skeleton while generation writes into this card. */
  isGeneratingOpen?: boolean;
}

/**
 * One category and the rules filed under it, rendered in the order given —
 * there is no client-side sort.
 *
 * The card is sortable among its siblings by the grip in the gutter, and is
 * also a drop target for rules, keyed by the category id, so a rule can be
 * dragged into it from another category even when it has no rules of its own
 * to drop beside. `useSortable` provides both: the droppable it registers is
 * the one the rule drag lands on.
 */
export function RuleCategoryGroup({
  category,
  disabled = false,
  focusNameSeq = 0,
  ghosts,
  isGeneratingOpen = false,
  onAddRule,
  onDeleteCategory,
  onDeleteRule,
  onRenameCategory,
  onSelectRule,
  rules,
  selectedRuleId,
}: RuleCategoryGroupProps): JSX.Element {
  const { currentStop, model } = useSuggestionReview();
  const categoryEdit = model.categoryEdits.get(category.id) ?? null;
  const kind = categoryEdit?.type ?? null;
  const headerStop =
    findStop(model, category.id, 'add') ??
    findStop(model, category.id, 'delete') ??
    findStop(model, category.id, 'patch');
  const moveStop = findStop(model, category.id, 'move');
  const { classes, cx } = useStyles({
    current: Boolean(headerStop && currentStop?.key === headerStop.key),
    kind,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // The rule whose delete is awaiting confirmation, if any.
  const [ruleToDelete, setRuleToDelete] = useState<PlaybookDetailRule | null>(null);
  const { isDragging, isOver, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: category.id,
  });
  const ruleCountLabel = `${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`;

  const suggestedName =
    categoryEdit?.type === 'change' ? categoryEdit.patch?.value.name : undefined;

  // Real rows and ghost destinations in one list, numbered in sequence.
  const entries = useMemo(() => interleaveByRank(rules, ghosts), [ghosts, rules]);

  // Nothing can be added to a category that is itself the suggestion.
  const cardKind = getCardKind(kind);
  const addRuleDisabledReason = cardKind ? ADD_RULE_DISABLED_REASONS[cardKind] : null;

  const handleAddRule = useCallback(() => onAddRule(category.id), [category.id, onAddRule]);
  const handleRenameCategory = useCallback(
    (newName: string) => onRenameCategory(category.id, newName),
    [category.id, onRenameCategory]
  );

  // A blank rule has nothing to lose, so it goes without asking.
  const handleDeleteRuleClick = useCallback(
    (rule: PlaybookDetailRule): void => {
      if (getIsBlankRule(rule)) onDeleteRule(category.id, rule.id);
      else setRuleToDelete(rule);
    },
    [category.id, onDeleteRule]
  );

  // An empty category has nothing to lose, so it goes without asking.
  const isEmpty = rules.length === 0;
  const handleDeleteCategoryClick = useCallback((): void => {
    if (isEmpty) onDeleteCategory(category.id);
    else setDeleteConfirmOpen(true);
  }, [category.id, isEmpty, onDeleteCategory]);

  return (
    <div
      ref={setNodeRef}
      className={cx(classes.group, isDragging && classes.groupDragging)}
      data-category-id={category.id}
      data-cy={Select.playbooks.categoryCard}
      /**
       * Translate only: dnd-kit's scale is the ratio between the card's height
       * before and after the reorder, and cards holding different numbers of
       * rules would stretch or squash on drop.
       */
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {/* Listeners only, not `attributes`: those would make the card a
          focusable role="button" around its real controls, and announce
          keyboard pick-up instructions that no keyboard sensor here honours. */}
      <span
        aria-hidden
        className={classes.dragHandle}
        data-cy={Select.playbooks.categoryDragHandle}
        {...listeners}
      >
        <GripVertical className={classes.dragHandleIcon} size={16} />
      </span>
      <div className={classes.header}>
        <div className={classes.categoryName} data-cy={Select.playbooks.categoryName}>
          <CategoryName
            category={category}
            disabled={disabled}
            focusNameSeq={focusNameSeq}
            kind={kind}
            suggestedName={suggestedName}
            onRename={handleRenameCategory}
          />
        </div>
        <CategoryHeaderAction
          disabled={disabled}
          kind={kind}
          stop={headerStop}
          onDelete={handleDeleteCategoryClick}
        />
      </div>

      <SortableContext items={rules.map(rule => rule.id)} strategy={verticalListSortingStrategy}>
        <div className={classes.rules}>
          {entries.length === 0 && !isGeneratingOpen && (
            <div
              className={cx(classes.noRules, isOver && classes.noRulesDropTarget)}
              data-cy={Select.playbooks.categoryEmpty}
            >
              No rules in this category yet.
            </div>
          )}
          {entries.map(entry => {
            if (entry.item)
              return (
                <SortableRuleRow
                  key={entry.item.id}
                  // Rows inside a card tinted as a whole colour themselves to match it.
                  containerKind={getCardKind(kind)}
                  disabled={disabled}
                  // Counts real rules only: a ghost destination takes no number.
                  ordinal={rules.indexOf(entry.item) + 1}
                  rule={entry.item}
                  selected={entry.item.id === selectedRuleId}
                  onDelete={handleDeleteRuleClick}
                  onSelect={onSelectRule}
                />
              );
            const ghost = entry.ghost;
            if (!ghost) return null;
            const ghostStop = findStop(model, ghost.rule.id, 'move');
            if (!ghostStop) return null;
            return (
              <RuleGhostRow
                key={`ghost-${ghost.rule.id}`}
                ruleId={ghost.rule.id}
                stop={ghostStop}
              />
            );
          })}
          {isGeneratingOpen && <Skeleton className={classes.ruleSkeleton} variant="rounded" />}
        </div>
      </SortableContext>

      <div className={classes.addRuleRow}>
        <AlineTooltip
          arrow
          placement="top"
          /**
           * Pulled 8px closer than the default: the button's top padding is
           * empty space, so at the default distance the tip floats well above
           * the words it points at.
           */
          slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }}
          title={addRuleDisabledReason ?? ''}
        >
          <span
            className={cx(
              classes.addRuleButtonWrap,
              Boolean(addRuleDisabledReason) && classes.addRuleButtonWrapDisabled
            )}
          >
            <Button
              className={classes.addRuleBottomButton}
              data-cy={Select.playbooks.addRule}
              disabled={disabled || Boolean(addRuleDisabledReason)}
              size="small"
              startIcon={<CirclePlus size={16} />}
              onClick={handleAddRule}
            >
              Add Rule
            </Button>
          </span>
        </AlineTooltip>
      </div>

      {moveStop && (
        <div className={classes.moveStrip}>
          <MoveSuggestionStrip current={currentStop?.key === moveStop.key} stop={moveStop} />
        </div>
      )}

      <ConfirmationDialog
        message={`Are you sure you want to delete "${category.name || 'Untitled category'}" and the ${ruleCountLabel} under it? This action cannot be undone.`}
        open={deleteConfirmOpen}
        primaryDataCy={Select.playbooks.categoryDeleteConfirm}
        title="Delete category?"
        onClickPrimary={() => {
          setDeleteConfirmOpen(false);
          onDeleteCategory(category.id);
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
      <ConfirmationDialog
        message={`Are you sure you want to delete "${ruleToDelete ? getRuleLabel(ruleToDelete) : ''}"? This action cannot be undone.`}
        open={ruleToDelete !== null}
        primaryDataCy={Select.playbooks.ruleDeleteConfirm}
        title="Delete rule?"
        onClickPrimary={() => {
          if (ruleToDelete) onDeleteRule(category.id, ruleToDelete.id);
          setRuleToDelete(null);
        }}
        onClose={() => setRuleToDelete(null)}
      />
    </div>
  );
}

/** Same label the row shows: title, else the starting position, else Untitled. */
export function getRuleLabel(rule: PlaybookDetailRule): string {
  const title = rule.title.trim();
  if (title) return title;
  return getStartingPosition(rule.positions)?.ruleText.trim() || 'Untitled Rule';
}

/**
 * An add or delete covers the whole category, rules included, so the whole
 * card is tinted for that kind, whether or not it is the current stop. A
 * title change only touches the header, so the card gets nothing; see
 * `useStyles` for what the header does.
 */
function getCardKind(kind: SuggestedEditKind | null): SuggestedEditKind | null {
  return kind === 'change' ? null : kind;
}

/**
 * Nobody has named it and nothing is typed into any position, so nothing is
 * lost by deleting it.
 */
function getIsBlankRule(rule: PlaybookDetailRule): boolean {
  if (rule.title.trim()) return false;

  return rule.positions.every(
    position => !position.ruleText.trim() && !(position.aiGuidance ?? '').trim()
  );
}
